// Builds a Drill "mode" — the same shape as the static Classic/Blitz/
// Challenge entries in constants.js's MODES, but built at navigation
// time from a specific dictionary instead of being a fixed preset. This
// is what directly ties Learn to Play: study a list, then practice
// spelling exactly those words on the board.
//
// Unlike the other three modes, Drill:
//  - only accepts words that are actually in the chosen dictionary
//    (`wordSet`), not the general Stacks word list;
//  - draws its letter bag from that dictionary's own letters
//    (`letterWeights`), so tiles reflect the vocabulary being studied;
//  - doesn't persist a score/leaderboard (`persistsScore: false`) —
//    the config is different every time, so a shared high score
//    wouldn't mean anything.
import { LETTER_WEIGHTS, MIN_WORD_LEN } from "./constants";

/** `words` is a flat array of raw word strings straight from a dictionary's word list. */
export function buildDrillMode({ words = [], dictTitle = "", minWordLen, matchStrategy } = {}) {
  const cleanWords = words
    .map((w) => String(w).trim().toUpperCase())
    .filter((w) => /^[A-Z]+$/.test(w));

  const wordSet = new Set(cleanWords);

  const letterCounts = {};
  for (const word of cleanWords) {
    for (const letter of word) letterCounts[letter] = (letterCounts[letter] || 0) + 1;
  }
  // A dictionary with no usable words leaves letterCounts empty — fall
  // back to the general letter bag rather than leaving pickRandomLetter
  // with nothing to draw from.
  const letterWeights = Object.keys(letterCounts).length > 0 ? letterCounts : LETTER_WEIGHTS;

  return {
    id: "drill",
    name: "Drill",
    tagline: dictTitle
      ? `Spelling practice from "${dictTitle}".`
      : "Spelling practice from your own word list.",
    cols: 8,
    rows: 10,
    matchStrategy: matchStrategy === "exact" ? "exact" : "substring",
    letterWeights,
    wordSet,
    minWordLen: Math.max(MIN_WORD_LEN, parseInt(minWordLen, 10) || MIN_WORD_LEN),
    persistsScore: false,
    changeModeHref: "/wordy-whiz/dictionaries",
    changeModeLabel: "Choose a different list",
    dictTitle,
  };
}
